import React, { useEffect, useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ArrowRightIcon, PhoneIcon, ClockIcon, UsersIcon, ShieldCheckIcon } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/oV66CvE5T9DxmmC6cXmgrm/ChatGPT_Image_Mar_27__2026__07_43_24_PM-removebg-preview.png';
const slides = ['https://cdn.magicpatterns.com/uploads/rbjCYEEPj7cdbWZWX7Uuym/8.jpg', 'https://cdn.magicpatterns.com/uploads/2kGX94xbQuji2osUz8iKU6/1.jpg', 'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?q=80&w=2000&auto=format&fit=crop', 'https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=2000&auto=format&fit=crop'];
export function HeroSection() {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);
  return <section id="home" className="relative min-h-screen flex items-center pt-40 lg:pt-48 pb-16 md:pb-24 overflow-hidden" data-id="element-170">
      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(20px) rotate(-5deg); }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.05); }
        }
        .animate-float { animation: float 8s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 10s ease-in-out infinite; }
        .animate-pulse-soft { animation: pulse-soft 6s ease-in-out infinite; }
      `
    }} data-id="element-171" />

      {/* Background Image Slider */}
      <div className="absolute inset-0 z-0 bg-dark" data-id="element-172">
        {slides.map((slide, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} data-id="element-173">
            <img src={slide} alt="Background slide" className="w-full h-full object-cover" data-id="element-174" />
          </div>)}

        {/* Gradient overlay to ensure text readability while letting images show through */}
        <div className="absolute inset-0 bg-gradient-to-r from-dark/95 via-dark/80 to-dark/50" data-id="element-175"></div>

        {/* Subtle grid/mesh pattern overlay */}
        <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: `
              linear-gradient(to right, #ffffff 1px, transparent 1px),
              linear-gradient(to bottom, #ffffff 1px, transparent 1px)
            `,
        backgroundSize: '40px 40px'
      }} data-id="element-176"></div>

        {/* Floating Decorative Blobs */}
        <div className="absolute top-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-float" data-id="element-177"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-float-delayed" data-id="element-178"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl animate-pulse-soft pointer-events-none" data-id="element-179"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-180">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-181">
          {/* LEFT SIDE: Text Content */}
          <div className="max-w-2xl" data-id="element-182">
            <div className={`inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-5 py-2.5 mb-8 border border-white/20 shadow-lg transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-183">
              <span className="relative flex h-3 w-3" data-id="element-184">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" data-id="element-185"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" data-id="element-186"></span>
              </span>
              <span className="text-white text-sm font-bold tracking-wider uppercase" data-id="element-187">
                Trusted Nursing Services
              </span>
            </div>

            <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 transition-all duration-1000 delay-100 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-188">
              Compassionate Care
              <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white/90 mt-4 font-extrabold tracking-tight" style={{
              fontFamily: "'Inter', sans-serif"
            }} data-id="element-189">
                for a Better Quality of Life
              </span>
            </h1>

            <p className={`text-lg md:text-xl text-white/80 mb-10 max-w-xl leading-relaxed font-medium transition-all duration-1000 delay-200 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-190">
              Personalized, professional, and trusted nursing services delivered
              with care and dignity in the comfort of your home.
            </p>

            <div className={`flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 transition-all duration-1000 delay-300 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-191">
              <a href="#/contact" className="bg-white text-primary-dark hover:bg-light-alt px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transform hover:-translate-y-1 flex items-center justify-center group" data-id="element-192">
                <PhoneIcon className="mr-2 h-5 w-5 text-primary group-hover:scale-110 transition-transform" data-id="element-193" />
                Contact Us Today
              </a>
              <a href="#/services" className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 backdrop-blur-md flex items-center justify-center group shadow-lg" data-id="element-194">
                Explore Our Services
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-195" />
              </a>
            </div>
          </div>

          {/* RIGHT SIDE: Glassmorphism Card */}
          <div className={`hidden lg:block transition-all duration-1000 delay-500 ease-out ${isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-12 scale-95'}`} data-id="element-196">
            <div className="relative w-full max-w-md mx-auto" data-id="element-197">
              {/* Decorative glow behind card */}
              <div className="absolute -inset-1 bg-gradient-to-r from-white/30 to-primary-light rounded-[2rem] blur-xl opacity-30 animate-pulse-soft" data-id="element-198"></div>

              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2rem] p-8 shadow-2xl overflow-hidden" data-id="element-199">
                {/* Glass reflection effect */}
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" data-id="element-200"></div>
                <div className="absolute -top-24 -left-24 w-48 h-48 bg-white/20 rounded-full blur-2xl" data-id="element-201"></div>

                <div className="flex flex-col items-center justify-center text-center py-4" data-id="element-202">
                  <h3 className="text-2xl font-bold text-white mb-10 tracking-wide" style={{
                  fontFamily: "'Cinzel', serif"
                }} data-id="element-203">
                    Excellence in Care
                  </h3>

                  {/* Mini Stat Cards */}
                  <div className="grid grid-cols-3 gap-3 w-full" data-id="element-204">
                    <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center hover:bg-white/20 transition-colors group" data-id="element-205">
                      <ClockIcon className="h-6 w-6 text-white/80 mb-2 group-hover:scale-110 transition-transform" data-id="element-206" />
                      <span className="text-xl font-bold text-white leading-none mb-1" data-id="element-207">
                        10+
                      </span>
                      <span className="text-[10px] text-white/80 uppercase tracking-wider font-semibold text-center leading-tight" data-id="element-208">
                        Years
                        <br data-id="element-209" />
                        Exp
                      </span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center hover:bg-white/20 transition-colors group" data-id="element-210">
                      <UsersIcon className="h-6 w-6 text-white/80 mb-2 group-hover:scale-110 transition-transform" data-id="element-211" />
                      <span className="text-xl font-bold text-white leading-none mb-1" data-id="element-212">
                        500+
                      </span>
                      <span className="text-[10px] text-white/80 uppercase tracking-wider font-semibold text-center leading-tight" data-id="element-213">
                        Families
                        <br data-id="element-214" />
                        Served
                      </span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center hover:bg-white/20 transition-colors group" data-id="element-215">
                      <ShieldCheckIcon className="h-6 w-6 text-white/80 mb-2 group-hover:scale-110 transition-transform" data-id="element-216" />
                      <span className="text-xl font-bold text-white leading-none mb-1" data-id="element-217">
                        24/7
                      </span>
                      <span className="text-[10px] text-white/80 uppercase tracking-wider font-semibold text-center leading-tight" data-id="element-218">
                        Care
                        <br data-id="element-219" />
                        Avail
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM STRIP: Trust Indicators */}
        <div className="mt-10 sm:mt-12 flex justify-center" data-id="element-220">
          <div className={`flex flex-wrap justify-center items-center gap-2 sm:gap-4 text-xs sm:text-sm font-medium text-white/70 tracking-widest uppercase transition-all duration-1000 delay-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-221">
            <span data-id="element-222">Licensed & Insured</span>
            <span className="hidden sm:inline text-white/30" data-id="element-223">•</span>
            <span data-id="element-224">RN Supervised</span>
            <span className="hidden sm:inline text-white/30" data-id="element-225">•</span>
            <span data-id="element-226">Background Checked Staff</span>
          </div>
        </div>
      </div>

      {/* Slider Pagination Squares */}
      <div className="absolute bottom-9 sm:bottom-12 left-1/2 -translate-x-1/2 flex space-x-3 z-30" data-id="element-227">
        {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 transition-all duration-300 shadow-sm ${index === currentSlide ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/70'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-228" />)}
      </div>

      {/* Decorative bottom curve */}
      <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-16 bg-light z-20" style={{
      clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 0 100%)'
    }} data-id="element-229"></div>
    </section>;
}